import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@pps/ui/components/ui/dialog';
import { Input } from '@pps/ui/components/ui/input';
import { Badge } from '@pps/ui/components/ui/badge';
import { Button } from '@pps/ui/components/ui/button';
import { ScrollArea } from '@pps/ui/components/ui/scroll-area';
import { ArrowLeft, LifeBuoy, Loader2, PlayCircle, Search, VolumeX } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import catalogo from '@/data/training-videos.json';

/**
 * Acervo de vídeos de treinamento do clube — aberto pelo ícone de ajuda no
 * header (`AppLayout`).
 *
 * Os arquivos NÃO vão no bundle: são 45 vídeos, ~1 GB. O manifesto
 * (`src/data/training-videos.json`, gerado por
 * `scripts/e2e/build-video-manifest.py`) carrega só metadado.
 *
 * Os vídeos vivem no bucket PRIVADO `training-videos` do Supabase, e a URL é
 * assinada na hora do play (1h de validade). Privado de propósito: o acervo é
 * do clube, então a policy exige ser staff ou dono de algum clube
 * (`is_any_club_staff`). Um jogador autenticado recebe 404 — conferido no HML.
 */

interface VideoItem {
  slug: string;
  arquivo: string;
  titulo: string;
  resumo: string;
  moduloId: number;
  modulo: string;
  duracaoSeg: number;
  narrado: boolean;
  processos: string[];
}

const VIDEOS = catalogo as VideoItem[];
const BUCKET = 'training-videos';
const VALIDADE_SEG = 60 * 60;

function duracao(seg: number): string {
  const m = Math.floor(seg / 60);
  const s = seg % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Busca acento-insensível sobre título, resumo, módulo e processos cobertos. */
function normaliza(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Abre o chamado de suporte. Opcional: sem ele o botão não aparece. */
  onReportarProblema?: () => void;
  /**
   * Slug para abrir já tocando, em vez da lista. Usado quando o atendente
   * indicou um vídeo no chamado: quem clica em "assistir" ali quer aquele
   * vídeo, não o acervo com ele em algum lugar.
   */
  slugInicial?: string | null;
}

export function TrainingVideosDialog({
  open,
  onOpenChange,
  onReportarProblema,
  slugInicial,
}: Props) {
  const { t } = useTranslation();
  const [busca, setBusca] = useState('');
  const [assistindo, setAssistindo] = useState<VideoItem | null>(null);

  // Só na ABERTURA. Se dependesse também de `assistindo`, o botão "voltar"
  // reabriria o mesmo vídeo em loop e a lista nunca apareceria.
  useEffect(() => {
    if (!open || !slugInicial) return;
    setAssistindo(VIDEOS.find((v) => v.slug === slugInicial) ?? null);
  }, [open, slugInicial]);
  const [url, setUrl] = useState<string | null>(null);
  const [erroUrl, setErroUrl] = useState<string | null>(null);

  // Assina na hora do play. Não dá para pré-assinar a lista inteira: seriam 45
  // chamadas por abertura do diálogo, e o link só vale 1h de qualquer forma.
  useEffect(() => {
    if (!assistindo) {
      setUrl(null);
      setErroUrl(null);
      return;
    }
    let cancelado = false;
    setUrl(null);
    setErroUrl(null);
    supabase.storage
      .from(BUCKET)
      .createSignedUrl(`${assistindo.slug}/${assistindo.arquivo}`, VALIDADE_SEG)
      .then(({ data, error }) => {
        if (cancelado) return;
        if (error || !data) setErroUrl(error?.message ?? 'unknown');
        else setUrl(data.signedUrl);
      });
    return () => {
      cancelado = true;
    };
  }, [assistindo]);

  const grupos = useMemo(() => {
    const alvo = normaliza(busca.trim());
    const filtrados = alvo
      ? VIDEOS.filter((v) =>
          normaliza(
            [v.titulo, v.resumo, v.modulo, ...v.processos].join(' '),
          ).includes(alvo),
        )
      : VIDEOS;

    const porModulo = new Map<string, VideoItem[]>();
    for (const v of filtrados) {
      const chave = v.modulo;
      if (!porModulo.has(chave)) porModulo.set(chave, []);
      porModulo.get(chave)!.push(v);
    }
    return [...porModulo.entries()];
  }, [busca]);

  const totalFiltrado = grupos.reduce((n, [, vs]) => n + vs.length, 0);

  function fechar(aberto: boolean) {
    if (!aberto) {
      setAssistindo(null);
      setBusca('');
    }
    onOpenChange(aberto);
  }

  return (
    <Dialog open={open} onOpenChange={fechar}>
      <DialogContent className="max-w-5xl h-[85dvh] flex flex-col gap-0 p-0">
        {assistindo ? (
          <>
            <DialogHeader className="px-6 pt-6 pb-3 shrink-0">
              <div className="flex items-start gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-0.5 shrink-0"
                  onClick={() => setAssistindo(null)}
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  {t('help.back')}
                </Button>
                <div className="min-w-0">
                  <DialogTitle className="truncate">{assistindo.titulo}</DialogTitle>
                  <DialogDescription className="mt-1">{assistindo.resumo}</DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <div className="flex-1 min-h-0 px-6 pb-6 overflow-y-auto">
              {url ? (
                <video
                  key={assistindo.slug}
                  src={url}
                  controls
                  autoPlay
                  className="w-full rounded-lg bg-black aspect-video"
                />
              ) : (
                <div className="w-full aspect-video rounded-lg border border-dashed flex items-center justify-center p-6 text-center text-sm text-muted-foreground">
                  {erroUrl ? (
                    t('help.loadFailed')
                  ) : (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('help.loading')}
                    </span>
                  )}
                </div>
              )}

              {assistindo.processos.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs font-medium text-muted-foreground mb-2">
                    {t('help.coveredProcesses')}
                  </p>
                  <ul className="space-y-1">
                    {assistindo.processos.map((p) => (
                      <li key={p} className="text-sm text-foreground">
                        • {p}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <DialogHeader className="px-6 pt-6 pb-3 shrink-0">
              {/* pr-12: o X do DialogContent tem 44px no canto direito. */}
              <div className="flex items-start justify-between gap-4 pr-12">
                <div className="min-w-0">
                  <DialogTitle>{t('help.title')}</DialogTitle>
                  <DialogDescription>
                    {t('help.subtitle', { count: VIDEOS.length })}
                  </DialogDescription>
                </div>
                {/* Saída para quem o acervo não resolveu. Fica aqui, e não num
                    menu à parte, porque o caminho natural é: procurei, não
                    achei, preciso de gente. */}
                {onReportarProblema && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0 gap-1.5"
                    onClick={onReportarProblema}
                  >
                    <LifeBuoy className="h-4 w-4" />
                    {t('help.reportProblem')}
                  </Button>
                )}
              </div>
              <div className="relative mt-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder={t('help.searchPlaceholder')}
                  className="pl-9"
                  aria-label={t('help.searchPlaceholder')}
                />
              </div>
            </DialogHeader>

            <ScrollArea className="flex-1 min-h-0">
              <div className="px-6 pb-6 space-y-6">
                {totalFiltrado === 0 && (
                  <p className="text-sm text-muted-foreground py-8 text-center">
                    {t('help.empty', { query: busca })}
                  </p>
                )}

                {grupos.map(([modulo, videos]) => (
                  <div key={modulo}>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                      {modulo}
                    </h3>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {videos.map((v) => (
                        <button
                          key={v.slug}
                          type="button"
                          onClick={() => setAssistindo(v)}
                          className="text-left rounded-lg border border-border p-3 hover:bg-accent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <div className="flex items-start gap-2">
                            <PlayCircle className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium leading-snug">{v.titulo}</p>
                              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                                {v.resumo}
                              </p>
                              <div className="flex items-center gap-1.5 mt-2">
                                <Badge variant="secondary" className="text-[10px] tabular-nums">
                                  {duracao(v.duracaoSeg)}
                                </Badge>
                                {!v.narrado && (
                                  <Badge variant="outline" className="text-[10px] gap-1">
                                    <VolumeX className="h-3 w-3" />
                                    {t('help.silent')}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
